import '../src/styles/base.css';
import '../src/styles/typography.css';
import '../src/styles/variables.css';
import '../src/styles/utilities.css';
import './App.css';

import { Link, Outlet } from "react-router-dom";
import { useState, useEffect } from 'react';
import { Menu, Transition, Disclosure } from '@headlessui/react';


// Updated ChevronDownIcon now accepts a 'flipped' prop to rotate the arrow.
const ChevronDownIcon = ({ flipped }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className="glue-icon glue-icon--18px"
    style={{ transform: flipped ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
  >
    <path d="m6 9 6 6 6-6"/>
  </svg>
);

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="hamburger-icon"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16m-16 6h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="hamburger-icon"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/* 
   ResourcesMenu:
   A clean Headless UI implementation that maintains the exact same styling
*/
function ResourcesMenu({ items }) {
  if (!items || !items.length) {
    return null;
  }

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button className="nav-button">
            Resources
            <ChevronDownIcon flipped={open} />
          </Menu.Button>
          
          <Transition
            show={open}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="resources-dropdown">
              <div className="resources-dropdown-inner">
                <ul className="resources-list">
                  {items.map((item) => (
                    <Menu.Item key={item.title} as="li" className="resource-item">
                      {({ active }) => (
                        item.external ? (
                          <a
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`resource-link ${active ? 'active' : ''}`}
                          >
                            <span className="resource-title">{item.title}</span>
                            {item.description && (
                              <span className="resource-description">{item.description}</span>
                            )}
                          </a>
                        ) : (
                          <Link
                            to={item.path}
                            className={`resource-link ${active ? 'active' : ''}`}
                          >
                            <span className="resource-title">{item.title}</span>
                            {item.description && (
                              <span className="resource-description">{item.description}</span>
                            )}
                          </Link>
                        )
                      )}
                    </Menu.Item>
                  ))}
                </ul>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}

/* 
   MobileResourcesMenu:
   A mobile-specific implementation using Headless UI Disclosure
*/
function MobileResourcesMenu({ items, onItemClick }) {
  if (!items || !items.length) {
    return null;
  }

  return (
    <Disclosure as="div" className="mobile-resources-disclosure">
      {({ open }) => (
        <>
          <Disclosure.Button className="nav-button mobile-resources-button">
            Resources
            <ChevronDownIcon flipped={open} />
          </Disclosure.Button>
          
          <Disclosure.Panel className="mobile-resources-dropdown">
            <ul className="mobile-resources-list">
              {items.map((item) => (
                <li key={item.title} className="mobile-resource-item">
                  {item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mobile-resource-link"
                      onClick={onItemClick}
                    >
                      <strong>{item.title}</strong>
                      {item.description && (
                        <span className="mobile-resource-description">
                          {item.description}
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="mobile-resource-link"
                      onClick={onItemClick}
                    >
                      <strong>{item.title}</strong>
                      {item.description && (
                        <span className="mobile-resource-description">
                          {item.description}
                        </span>
                      )}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

/* 
   Header:
   Renders the main header with primary navigation items.
   - The Resources item uses an expanded panel styled like the original GDM page,
     now without a View All link.
   - About, People, and Press are plain links.
   - A mobile hamburger menu is provided.
*/
function Header() {
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [, setIsMobileView] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768;
      setIsMobileView(isMobile);
      
      // Close mobile menu if switching to desktop view
      if (!isMobile && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.nav-items')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (isMobileMenuOpen && event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  // Track theme changes
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem('theme');
      const bodyHasLightTheme = document.body.classList.contains('light-theme');
      setIsDarkTheme(savedTheme === 'dark' || (!savedTheme && !bodyHasLightTheme));
    };

    // Initial check
    checkTheme();

    // Observer to watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Clean up
    return () => observer.disconnect();
  }, []);

  const navItems = [
    {
      title: "Resources",
      expanded: true,
      expandedContent: {
        items: [
          {
            title: "AI Law Tracker",
            description: "Track AI-related legal developments and cases",
            path: "/ai-law-tracker.html",
            external: true
          },
          {
            title: "Policy Briefs",
            description: "Our policy recommendations and briefs",
            path: "/policy-briefs"
          },
          {
            title: "Blog",
            description: "Latest insights and analysis from our team",
            path: "/blog"
          },
          {
            title: "Newsletter",
            description: "Subscribe to our AI law and policy updates",
            path: "https://substack.com/@ailawpolicy",
            external: true
          },
        ],
      },
    },
    { title: "Publications", path: "/publications" },
    { title: "Teaching", path: "/teaching" },
    // { title: "Datasets", path: "/datasets" },
    { title: "People", path: "/people" },
    { title: "Press", path: "/press" },
    { title: "About", path: "/about" }
  ];

  const handleMobileItemClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="header-container">
        <nav className="nav">
          <Link to="/" className="logo">
            <img 
              src={isDarkTheme ? "/Logo alternative.png" : "/Geometric Diamond Logo Design.png"} 
              alt="Polaris Lab Logo" 
            />
            <span className="logo-text">Polaris</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-items desktop-nav-items">
            {navItems.map((item) => (
              <div key={item.title} className="nav-item">
                {item.expanded ? (
                  <ResourcesMenu items={item.expandedContent?.items || []} />
                ) : (
                  item.external ? (
                    <a
                      href={item.path}
                      className="nav-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.title}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="nav-link"
                    >
                      {item.title}
                    </Link>
                  )
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu */}
          <div className="mobile-menu">
            <button 
              className="mobile-menu-button" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
            
            {isMobileMenuOpen && (
              <div className="nav-items mobile-nav-items">
                {navItems.map((item) => (
                  <div key={item.title} className="nav-item">
                    {item.expanded ? (
                      <MobileResourcesMenu 
                        items={item.expandedContent?.items || []} 
                        onItemClick={handleMobileItemClick}
                      />
                    ) : (
                      item.external ? (
                        <a
                          href={item.path}
                          className="nav-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleMobileItemClick}
                        >
                          {item.title}
                        </a>
                      ) : (
                        <Link
                          to={item.path}
                          className="nav-link"
                          onClick={handleMobileItemClick}
                        >
                          {item.title}
                        </Link>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <p>POLARIS Lab; Peter Henderson &copy; {new Date().getFullYear()}</p>
    </footer>
  );
}

export default function App() {
  // Ensure dark theme is always active
  useEffect(() => {
    document.body.className = '';
    localStorage.setItem('theme', 'dark');
  }, []);

  return (
    <div className="App">
      <Header />
      <Outlet />
      <Footer />
    </div>
  );
}